import {
  Button,
  Checkbox,
  Divider,
  Grid,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
// Icons
import IconButton from '@material-ui/core/IconButton';
import BlockIcon from '@material-ui/icons/Block';
import CheckIcon from '@material-ui/icons/Check';
import CSS from 'csstype';
// Fuzzy Search
import { Searcher } from 'fast-fuzzy';
import { cloneDeep } from 'lodash-es';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { QuickFilterDropdown, useStyles } from '~/src/dash/components/quick-filter-dropdown';
import {
  isWithinMediaSizeOrBelow,
  spacing,
  Text,
  TextButton,
  TextInput,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { compareAlphanums, compareDates } from '~/src/dash/utils/basic';
import { publicaColors } from '~/src/dash/utils/colors';
import { formatForClassname } from '~/src/dash/utils/format';
import { containsNonLatinCodepoints } from '~/src/dash/utils/string';
import { useBaseState } from '~/src/dash/views/base/contexts';

export interface IQuickFilter {
  title: string;
  optionItems: IMenuList;
  show: boolean;
  type: string;
}

interface IProps {
  // SEARCH PROPS
  //
  // Function to get the value from a search element. Used for the search
  // default: take the value from the title field
  getValueFromSearchElement?: (src: any) => string;
  fontSize?: string;
  // font color when nothing is selected
  fontColor?: string;
  menuTitle: string;
  // Custom prefix for title - default is 'Select'
  titlePrefix?: string;
  // Input list
  list: IMenuList;
  // Enable Select All button?
  // default=false
  selectAll?: boolean;
  // Is MultiSelect allowed?
  // default=true
  enableMultiSelect?: boolean;
  initialDisplayLimit?: number;
  // On change, input is modified list (with .selected set and up to date)
  onChange: (values: IMenuList) => void;
  // On Accept OR click outside
  handleMenuAcceptClick?: (values: IMenuList) => void;

  handleMenuOpen?: () => void;

  handleMenuAccept: (listOverride?: IMenuList) => void;

  handleClickOutside: (this: Document, e: MouseEvent) => void;

  handleShow: (title: string) => void;

  isOpen: boolean;

  // ref for opening the handleClickOutside in parent comp
  mainDropdownMenuRef?: React.RefObject<HTMLDivElement>;

  // Allow/Deny logic PROPS
  allowDenyLogicEnabled?: boolean;
  onChangeAllowDenyToggle?: (isAllow: boolean) => void;
  isAllowedSelected?: boolean;

  // Some fields are default "All" even if nothing is selected, like countries filter on campaigns or bidders. Used to show "All" by default when menu closed
  isNoneSelectedAllSelected?: boolean;
  helperText?: string;

  // Height of the list
  // default: 400px
  maxHeight?: number;

  // width of the component
  width?: string;

  // disabled turns off opening of menu
  disabled?: boolean;
  // disabled adds tooltip to disabled item  in list
  disabledTooltip?: string;
  // disables search input box
  disableSearch?: boolean;
  // diables quick filter
  disableSortBy?: boolean;
  // quick filter
  quickFilterMenuRef?: React.RefObject<HTMLDivElement>;
  quickFilterDropdownMenuRef?: React.RefObject<HTMLDivElement>;
  quickFilterButtonRef?: React.RefObject<HTMLDivElement>;
  sortByButtonRef?: React.RefObject<HTMLDivElement>;
  sortByMenuRef?: React.RefObject<HTMLDivElement>;
  quickFilterMenuItems: IQuickFilter[];
  onChangeQuickFilter?: (
    values: Array<{ title: string; optionItems: IMenuList; type: string }>,
  ) => void;
  setFilterOptionItems: React.Dispatch<React.SetStateAction<IQuickFilter[]>>;
  overrideActiveResourceFilter?: ActiveResourceFilter;
  disableAlphaToggle?: boolean;

  // QF= Quick Filter
  handleQFDropdownOpen?: () => void;

  // On Accept OR click outside of Quick Filter Dropdown
  handleQFDropdownAccept: (listOverride?: IMenuList) => void;

  isOpenQFMenu?: boolean;
  isOpenQFDropdown?: boolean;
  isOpenSort?: boolean;

  // enabled to make list static (not a dropdown)
  enableStatic?: boolean;
  // enables fixed for filter button dropdown
  enableFixed?: boolean;

  // enabled to make list read only
  enableReadOnly?: boolean;

  // limits the number of selections that can be made on the menu
  selectLimit?: number;

  // function that is called when Menu is opened
  onOpen?: () => void;

  // Loading material search
  loading?: boolean;
  leftRightDropdownEnabled?: boolean;
  // Determines if the dropdown should open to the left or right, only enabled by parent prop leftRightDropdownEnabled
  // if the component is on the left half of the screen, dropdown will open with left side as root vs right side
  isRightSide?: boolean;
  rightPos?: number;
  bottomPos?: number;
  responsivePositionEnabled?: boolean;
  // style object containing left/right absolute positioning
  responsiveStylePosition?: CSS.Properties;
  // text for a top item in a menu and a function passed to handle if it is clicked
  topListItemText?: string;
  topListItemClickHandler?: () => void;
  overrideDefaultSortMode?: SortByMode;

  // keeps the dropdown open even on clicking outside if set to true
  keepDropdownOpen?: boolean;

  // For keeping creatives with the same advertiser as the parent order on the top of the list
  orderAdvertiserID?: number;

  // shows ID prefix in title
  showPrefixID: boolean;
}

enum CheckBoxStatus {
  UNCHECKED,
  SEMI_CHECKED,
  CHECKED,
}

/**
 * Compute the checkbox icon
 * @param isAllowEnabled is Allow/Deny logic enabled
 * @param allowedSelected is Allow selected (Allow/Deny logic)
 */
function computeCheckboxIcon(isAllowEnabled: boolean | undefined, allowedSelected: boolean) {
  if (isAllowEnabled === true) {
    if (allowedSelected) {
      return <CheckIcon style={{ color: publicaColors.green }} />;
    } else {
      return <BlockIcon style={{ color: publicaColors.red }} />;
    }
  } else {
    return undefined;
  }
}

export enum SortByMode {
  Default = 0,
  AtoZ = 1,
  ZtoA = 2,
  LastUpdated = 3,
  LastCreated = 4,
  TopRevenue = 5,
  AdvertiserFirst = 6,
  IdAsc = 7,
  IdDesc = 8,
}

export enum ActiveResourceFilter {
  Default = 0,
  Active = 1,
  Inactive = 2,
  ShowAll = 3,
}

// this should be refactored to find the title directly instead of splitting the string
function cleanTitle(item: IListItem) {
  // check if the title is a country that starts with an emoji (unicode)
  if (containsNonLatinCodepoints(item.title)) {
    return item.title.split(' ', -1).slice(1).join(' ');
  }
  if (item.title.indexOf(`[${item.ID}]`) !== -1 && item.title.split('] ')[1] !== undefined) {
    return item.title.split('] ')[1].toUpperCase();
  } else {
    return item.title.toUpperCase();
  }
}

function createdAtDate(item: IListItem) {
  if (item.site) {
    return new Date(item.site.CreatedAt);
  } else {
    return new Date(0);
  }
}

function updatedAtDate(item: IListItem) {
  if (item.site && item.site.UpdatedAt) {
    return new Date(item.site.UpdatedAt);
  } else if (item.site) {
    return new Date(item.site.CreatedAt);
  } else {
    return new Date(0);
  }
}

function totalRevenue(item: IListItem) {
  if (item.site && Object.values(item.site.ChannelPerformanceSortByOptions).length !== 0) {
    return item.site.ChannelPerformanceSortByOptions.TotalRevenue;
  }
  return 0;
}

interface IListItemWithSubheader {
  subheader: string;
  selectOption: IListItem[];
}

function createListItemWithSubheader(itemList: IListItem[]) {
  const newItemList = [] as IListItemWithSubheader[];

  itemList.forEach((menuItem) => {
    if (menuItem.subheader !== undefined) {
      const menuItemIndex = newItemList.findIndex((item) => item.subheader === menuItem.subheader);
      if (menuItemIndex !== -1) {
        newItemList[menuItemIndex].selectOption.push(menuItem);
      } else {
        const newMenuItems = {
          subheader: menuItem.subheader,
          selectOption: [menuItem],
        } as IListItemWithSubheader;
        newItemList.push(newMenuItems);
      }
    }
  });
  return newItemList;
}

interface ISortOptions {
  label: string;
  sortMode: number;
  alwaysShow?: boolean;
  menuTitles?: string[];
}

// menuTitles: if sort option is exclusive to certain menuTitles set alwaysShow: false and add them to the array
const sortOptions: ISortOptions[] = [
  // alphabetical sort is currently always shown
  {
    label: 'Last Updated',
    sortMode: SortByMode.LastUpdated,
    alwaysShow: false,
    menuTitles: ['Channels', 'Channel'],
  },
  {
    label: 'Last Created',
    sortMode: SortByMode.LastCreated,
    alwaysShow: false,
    menuTitles: ['Channels', 'Channel'],
  },
  {
    label: 'Top Revenue',
    sortMode: SortByMode.TopRevenue,
    alwaysShow: false,
    menuTitles: ['Channels', 'Channel'],
  },
  {
    label: 'Ascending IDs',
    sortMode: SortByMode.IdAsc,
    alwaysShow: false,
    menuTitles: ['IAB Sub Catgeory', 'Priorities'],
  },
  {
    label: 'Descending IDs',
    sortMode: SortByMode.IdDesc,
    alwaysShow: false,
    menuTitles: ['IAB Sub Catgeory', 'Priorities'],
  },
];

export const MaterialSearchDropdownMultiList: FunctionComponent<IProps> = (props: IProps) => {
  // props
  const {
    onChange,
    isRightSide,
    rightPos,
    bottomPos,
    responsivePositionEnabled,
    responsiveStylePosition,
    menuTitle,
    list,
    isOpen,
    handleMenuAccept,
    loading,
    mainDropdownMenuRef,
    enableStatic,
    enableFixed,
    disableSearch,
    disableSortBy,
    topListItemText,
    topListItemClickHandler,
    enableMultiSelect,
    quickFilterMenuRef,
    quickFilterButtonRef,
    quickFilterDropdownMenuRef,
    isOpenQFMenu,
    sortByMenuRef,
    sortByButtonRef,
    isOpenSort,
    handleQFDropdownAccept,
    handleClickOutside,
    handleShow,
    quickFilterMenuItems,
    onChangeQuickFilter,
    setFilterOptionItems,
    overrideDefaultSortMode,
    disabledTooltip,
    keepDropdownOpen,
    orderAdvertiserID,
    overrideActiveResourceFilter,
    disableAlphaToggle,
  } = props;

  const { t } = usePublicaTranslation();

  // State
  const classes = useStyles();
  const initialDisplayLimitNum = props.initialDisplayLimit || 80;
  const [bottomIndexDisplayed, setBottomIndexDisplayed] = useState<number>(initialDisplayLimitNum);
  const nullRef = useRef<HTMLDivElement>(null);
  const menuListRef = useRef<HTMLUListElement>(null);
  const [scrollPosition, setScrollPosition] = useState<number>(0);
  const [activeSortMode, setActiveSortMode] = useState<SortByMode>(
    overrideDefaultSortMode || SortByMode.Default,
  );
  const [activeResourceFilter, setActiveResourceFilter] = useState<ActiveResourceFilter>(
    overrideActiveResourceFilter || ActiveResourceFilter.Default,
  );
  const [toggleAsc, setToggleAsc] = useState<boolean>(true);

  // sets up listener events to open and close dropdown menu
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside, false);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, false);
    };
  });

  // Search Logic
  const [searchInput, setSearchInput] = useState<string>('');
  const [fuzzySearcher, setFuzzySearcher] = useState<Searcher<IListItem, any>>(undefined as any);

  // Select All Logic
  const [selectAllCheckedStatus, setSelectAllCheckedStatus] = useState<CheckBoxStatus>(
    CheckBoxStatus.UNCHECKED,
  );

  // Allow Deny Logic
  const [isAllowed, setAllowed] = useState<boolean>(props.isAllowedSelected === true);

  // if the menu title changes, initial values should be cleared
  // does not unmount/mount new lists so the searchinput was carrying over
  useEffect(() => {
    setBottomIndexDisplayed(initialDisplayLimitNum);
    setSearchInput('');
    setSelectAllCheckedStatus(CheckBoxStatus.UNCHECKED);
    setAllowed(props.isAllowedSelected === true);
    setActiveResourceFilter(overrideActiveResourceFilter || ActiveResourceFilter.Default);
  }, [menuTitle]);

  // If the MenuList changes (updates) and there is a reference on the list, set the scroll position of the list
  // Done to keep scroll position between re-renders caused by handleItemClick
  useEffect(() => {
    if (menuListRef.current) {
      menuListRef.current.scrollTop = scrollPosition;
    }
    const fuzzyV2 = new Searcher(Object.values(list), {
      keySelector: (obj) => {
        let selector = '';
        if (obj.ID !== undefined) {
          selector += obj.ID;
        }
        selector += obj.title;
        return selector;
      },
      threshold: 0.8,
    });

    setFuzzySearcher(fuzzyV2);
  }, [list]);

  // Do we need to check / uncheck the All Selected Box ?
  // The box is checked if all filtered items are checked
  // uncheck otherwise
  // need to be updated each time we check one box and each time we change the search
  const computeIsAllSelected = () => {
    if (!props.selectAll) {
      return;
    }

    let listValues = Object.values(list);
    if (activeResourceFilter === ActiveResourceFilter.Active) {
      listValues = listValues.filter((val) => val.active);
    } else if (activeResourceFilter === ActiveResourceFilter.Inactive) {
      listValues = listValues.filter((val) => !val.active);
    }

    let numSelected = 0;

    for (const n of listValues) {
      if (list[n.ID] !== undefined && list[n.ID].selected) {
        numSelected += 1;
      }
    }
    // all items are selected
    // Don't update state if don't need to
    if (numSelected === listValues.length) {
      if (selectAllCheckedStatus !== CheckBoxStatus.CHECKED) {
        setSelectAllCheckedStatus(CheckBoxStatus.CHECKED);
      }
      return;
    }

    // some items are selected
    if (numSelected > 0) {
      if (selectAllCheckedStatus !== CheckBoxStatus.SEMI_CHECKED) {
        setSelectAllCheckedStatus(CheckBoxStatus.SEMI_CHECKED);
      }
      return;
    }

    // no items are selected
    if (numSelected === 0) {
      if (selectAllCheckedStatus !== CheckBoxStatus.UNCHECKED) {
        setSelectAllCheckedStatus(CheckBoxStatus.UNCHECKED);
      }
    }
  };

  // Adds/removes selected from item in hash then sets state in parent component
  const handleItemClick = (id: string): void => {
    if (props.enableReadOnly) {
      return;
    }
    const newList = cloneDeep(list);
    if (props.enableMultiSelect === false) {
      // Unselect everything.
      for (const obj of Object.values(newList)) {
        newList[obj.ID].selected = false;
      }
      newList[id].selected = true;
    } else {
      let allowSelect = true;
      if (props.selectLimit && props.selectLimit !== -1) {
        let selectedCount = 0;
        for (const obj of Object.values(newList)) {
          if (newList[obj.ID].selected) {
            selectedCount++;
            if (selectedCount === props.selectLimit) {
              allowSelect = false;
              break;
            }
          }
        }
      }
      // Multi select enabled, invert selected status
      // if select is allowed OR if the item is already selected
      if (allowSelect || newList[id].selected) {
        newList[id].selected = !newList[id].selected;
      }
    }

    onChange(newList);
    if (props.enableMultiSelect === false || (props.selectLimit && props.selectLimit === 1)) {
      // Accept
      handleMenuAccept(newList);
    }
  };

  // Logic:
  // When checkbox is X and we click on it:
  // - UNCHECKED: select all boxes (filtered) and select all status = CHECKED || SEMI-CHECK
  // - CHECKED: unselect all boxes and select all status = UNCHECK
  // - SEMI_CHECK: select everything (filtered and non filtered) select all status = CHECKED
  const handleSelectAllClick = (filteredList: IListItem[]): void => {
    if (props.enableReadOnly) {
      return;
    }
    const newList = cloneDeep(list);
    const listValues = Object.values(list);

    // Semi Checked: Select everything
    if (selectAllCheckedStatus === CheckBoxStatus.SEMI_CHECKED) {
      for (const i of listValues) {
        newList[i.ID].selected = true;
      }
    }

    // Unchecked: Select filtered
    if (selectAllCheckedStatus === CheckBoxStatus.UNCHECKED) {
      for (const i of filteredList) {
        newList[i.ID].selected = true;
      }
    }

    // Checked: Unselect all
    if (selectAllCheckedStatus === CheckBoxStatus.CHECKED) {
      for (const i of listValues) {
        newList[i.ID].selected = false;
      }
    }

    onChange(newList);
    computeIsAllSelected();
  };

  const handleActiveQuickFilterClick = (activeResourceFilter: ActiveResourceFilter): void => {
    setActiveResourceFilter(activeResourceFilter);

    computeIsAllSelected();
  };

  // handles changes to filter and sets state in parent
  const handleTextInput = (input: string): void => {
    setSearchInput(input);
    setBottomIndexDisplayed(initialDisplayLimitNum);
  };

  // checks to see if list is near bottom, if it is, loads more menu items
  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    const bottom =
      e.currentTarget.scrollHeight - e.currentTarget.scrollTop === e.currentTarget.clientHeight;
    if (bottom) {
      setBottomIndexDisplayed(bottomIndexDisplayed + initialDisplayLimitNum);
    }
  };

  function createFilteredList(): IListItem[] {
    if (searchInput !== '') {
      if (fuzzySearcher === undefined) {
        const fuzzyV2 = new Searcher(Object.values(list), {
          keySelector: (obj) => {
            let selector = '';
            if (obj.ID !== undefined) {
              selector += obj.ID;
            }
            selector += obj.title;
            return selector;
          },
          threshold: 0.8,
        });

        setFuzzySearcher(fuzzyV2);
      }

      const searchResult = fuzzySearcher.search(searchInput);
      return Object.values(searchResult);
    } else {
      return Object.values(list);
    }
  }

  const filteredList = createFilteredList();
  computeIsAllSelected();

  const sortByAdvertiserFirst = (items: IListItem[], isAdvertiser: boolean): IListItem[] => {
    if (isAdvertiser) {
      return items
        .filter((item: IListItem) => item.advertiserID === orderAdvertiserID)
        .sort((a, b) => compareAlphanums(cleanTitle(a), cleanTitle(b), true));
    } else {
      return items
        .filter((item: IListItem) => item.advertiserID !== orderAdvertiserID)
        .sort((a, b) => compareAlphanums(cleanTitle(a), cleanTitle(b), true));
    }
  };

  const sortFilteredList = (filteredList: IListItem[]) => {
    if (activeResourceFilter === ActiveResourceFilter.Active) {
      filteredList = filteredList.filter((item) => item.active);
    } else if (activeResourceFilter === ActiveResourceFilter.Inactive) {
      filteredList = filteredList.filter((item) => !item.active);
    }

    const selectedItems = filteredList.filter((item: IListItem) => item && item.selected);
    const notSelectedItems = filteredList.filter((item: IListItem) => item && !item.selected);

    // below sets defaultSorts for clarification
    const [asc, desc, newest] = [true, false, true];
    switch (activeSortMode) {
      case SortByMode.Default:
      case SortByMode.AtoZ:
        if (searchInput !== '') {
          // if AtoZ search (default) and user entered text input, we don't sort
          break;
        }
        selectedItems.sort((a, b) => compareAlphanums(cleanTitle(a), cleanTitle(b), asc));
        notSelectedItems.sort((a, b) => compareAlphanums(cleanTitle(a), cleanTitle(b), asc));
        break;
      case SortByMode.ZtoA:
        selectedItems.sort((a, b) => compareAlphanums(cleanTitle(a), cleanTitle(b), desc));
        notSelectedItems.sort((a, b) => compareAlphanums(cleanTitle(a), cleanTitle(b), desc));
        break;
      case SortByMode.IdAsc:
        selectedItems.sort((a, b) => compareAlphanums(parseInt(a.ID, 10), parseInt(b.ID, 10), asc));
        notSelectedItems.sort((a, b) =>
          compareAlphanums(parseInt(a.ID, 10), parseInt(b.ID, 10), asc),
        );
        break;
      case SortByMode.IdDesc:
        selectedItems.sort((a, b) =>
          compareAlphanums(parseInt(a.ID, 10), parseInt(b.ID, 10), desc),
        );
        notSelectedItems.sort((a, b) =>
          compareAlphanums(parseInt(a.ID, 10), parseInt(b.ID, 10), desc),
        );
        break;
      case SortByMode.LastUpdated:
        selectedItems.sort((a, b) => compareDates(updatedAtDate(a), updatedAtDate(b), newest));
        notSelectedItems.sort((a, b) => compareDates(updatedAtDate(a), updatedAtDate(b), newest));
        break;
      case SortByMode.LastCreated:
        selectedItems.sort((a, b) => compareDates(createdAtDate(a), createdAtDate(b), newest));
        notSelectedItems.sort((a, b) => compareDates(createdAtDate(a), createdAtDate(b), newest));
        break;
      case SortByMode.TopRevenue:
        selectedItems.sort((a, b) => compareAlphanums(totalRevenue(a), totalRevenue(b), desc));
        notSelectedItems.sort((a, b) => compareAlphanums(totalRevenue(a), totalRevenue(b), desc));
        break;
      case SortByMode.AdvertiserFirst:
        const advertiserSelectedItems = sortByAdvertiserFirst(selectedItems, true);
        const nonadvertiserSelectedItems = sortByAdvertiserFirst(selectedItems, false);
        const advertiserNotSelectedItems = sortByAdvertiserFirst(notSelectedItems, true);
        const nonadvertiserNotSelectedItems = sortByAdvertiserFirst(notSelectedItems, false);
        return [
          ...advertiserSelectedItems,
          ...nonadvertiserSelectedItems,
          ...advertiserNotSelectedItems,
          ...nonadvertiserNotSelectedItems,
        ];
      default:
        break;
    }
    return [...selectedItems, ...notSelectedItems];
  };

  // Select All Logic
  const computeSelectAllText = (): string => {
    // Disabled
    return t('SelectAll');
  };

  const clearSelections = () => {
    const newList = cloneDeep(list);
    const newQuickFilterList = [] as Array<{ title: string; optionItems: IMenuList; type: string }>;

    for (let i = 0; i < Object.values(newList).length; i++) {
      newList[Object.values(newList)[i].ID].selected = false;
    }

    quickFilterMenuItems.forEach((filterOption) => {
      Object.values(filterOption.optionItems).forEach((item) => {
        filterOption.optionItems[item.ID].selected = false;
      });
      const quickFilterListItem = {
        title: filterOption.title,
        optionItems: filterOption.optionItems,
        type: filterOption.type,
      };
      newQuickFilterList.push(quickFilterListItem);
    });

    onChange(newList);
    if (onChangeQuickFilter) {
      onChangeQuickFilter(newQuickFilterList);
    }
  };

  const changeAllowStatus = (newStatus: boolean) => {
    setAllowed(newStatus);

    if (props.onChangeAllowDenyToggle !== undefined) {
      props.onChangeAllowDenyToggle(newStatus);
    }
  };

  // Allow List Logic
  const renderAllowDenyList = () => {
    return (
      <>
        <IconButton onClick={() => changeAllowStatus(true)} disabled={isAllowed}>
          <CheckIcon style={{ color: isAllowed ? 'green' : 'lightgray' }} />
        </IconButton>
        <IconButton
          onClick={() => changeAllowStatus(false)}
          disabled={!isAllowed}
          style={{ paddingLeft: '0px' }}
        >
          <BlockIcon style={{ color: !isAllowed ? 'red' : 'lightgray' }} />
        </IconButton>
      </>
    );
  };

  const renderQuickFilterButton = () => {
    return (
      <Grid
        item
        style={{
          marginLeft: 0,
          color: '#4952FF',
          width: '25px',
          height: '24px',
          borderRadius: '2px',
        }}
        ref={quickFilterButtonRef}
      >
        <img src='/assets/shared/svg/FilterIcon.svg' width='29px' />
      </Grid>
    );
  };

  // render dropdown for quick filter menu (ie. all the channel groups or all the channel labels)
  const renderQuickFilterDropdown = (option: IQuickFilter) => {
    return (
      <Grid ref={quickFilterDropdownMenuRef}>
        <QuickFilterDropdown
          menuTitle={option.title}
          list={list}
          onChangeMainDropdown={onChange}
          handleQFDropdownAccept={handleQFDropdownAccept}
          handleClickOutside={handleClickOutside}
          quickFilterItemsList={option.optionItems}
          quickFilterMenuItems={quickFilterMenuItems}
          enableMultiSelect={true}
          onChangeQuickFilter={onChangeQuickFilter}
          setFilterOptionItems={setFilterOptionItems}
        />
      </Grid>
    );
  };

  // render Quick Filter menu (ie 'Group' and 'Label' for channels)
  const renderQuickFilterMenu = (quickFilterMenuItems: IQuickFilter[]) => {
    return (
      <>
        <Grid
          container
          direction='column'
          spacing={3}
          alignItems='flex-end'
          style={{ marginLeft: 5 }}
        >
          <Tooltip title='Quick Filter' PopperProps={{ disablePortal: true }}>
            <Button style={{ minWidth: 40, marginLeft: 10, padding: 5 }}>
              {renderQuickFilterButton()}
            </Button>
          </Tooltip>
          {isOpenQFMenu && (
            <Grid ref={quickFilterMenuRef}>
              <Paper
                style={{
                  position: 'absolute',
                  width: 150,
                  marginTop: 0,
                  marginLeft: -40,
                  zIndex: 10,
                }}
              >
                <Typography className={classes.menuHeader}>Quick Filter</Typography>
                {quickFilterMenuItems.map((filterOptionItem) => {
                  if (filterOptionItem.type === 'Active') {
                    let currActiveResourceFilter: ActiveResourceFilter;
                    switch (filterOptionItem.title) {
                      case 'Active':
                        currActiveResourceFilter = ActiveResourceFilter.Active;
                        break;
                      case 'Inactive':
                        currActiveResourceFilter = ActiveResourceFilter.Inactive;
                        break;
                      case 'Show All':
                        currActiveResourceFilter = ActiveResourceFilter.ShowAll;
                        break;
                      default:
                        currActiveResourceFilter = ActiveResourceFilter.Default;
                    }
                    return (
                      <Grid container style={{ marginLeft: 10, marginRight: 10 }}>
                        <Grid item xs={1} style={{ paddingTop: 3, paddingLeft: 6 }}>
                          {activeResourceFilter === currActiveResourceFilter && (
                            <img src='/assets/shared/svg/circle.svg' width='5px' />
                          )}
                        </Grid>
                        <Grid
                          item
                          xs={11}
                          onClick={() => {
                            handleActiveQuickFilterClick(currActiveResourceFilter);
                          }}
                          style={{ paddingLeft: 2 }}
                        >
                          <Button
                            style={{
                              width: '80%',
                              textTransform: 'none',
                              justifyContent: 'flex-start',
                              margin: 0,
                            }}
                          >
                            {filterOptionItem.title}
                          </Button>
                        </Grid>
                      </Grid>
                    );
                  } else if (filterOptionItem.type === 'Channel') {
                    return (
                      <Grid
                        className={
                          filterOptionItem.show
                            ? classes.filterOptionButtonsSelected
                            : classes.filterOptionButtons
                        }
                      >
                        <Grid
                          onMouseEnter={() => handleShow(filterOptionItem.title)}
                          style={{ fontWeight: 500 }}
                        >
                          {filterOptionItem.title}
                        </Grid>
                        {filterOptionItem.show && (
                          <Grid onMouseLeave={() => handleShow('noHover')}>
                            {renderQuickFilterDropdown(filterOptionItem)}
                          </Grid>
                        )}
                      </Grid>
                    );
                  } else {
                    return;
                  }
                })}
              </Paper>
            </Grid>
          )}
        </Grid>
      </>
    );
  };

  const renderAlphaToggle = () => {
    const isActiveAsc = activeSortMode === SortByMode.AtoZ || activeSortMode === SortByMode.Default;
    const isActiveDesc = activeSortMode === SortByMode.ZtoA;
    const isActive = isActiveAsc || isActiveDesc;
    return (
      <Grid container style={{ marginLeft: 10, marginRight: 10 }}>
        <Grid item xs={1} style={{ paddingTop: 3, paddingLeft: 6 }}>
          {isActive && <img src='/assets/shared/svg/circle.svg' width='5px' />}
        </Grid>
        <Grid
          item
          xs={11}
          onClick={() => {
            setToggleAsc(!toggleAsc);
            if (isActiveAsc) {
              setActiveSortMode(SortByMode.ZtoA);
            } else if (isActiveDesc) {
              setActiveSortMode(SortByMode.AtoZ);
            } else {
              setActiveSortMode(toggleAsc ? SortByMode.AtoZ : SortByMode.ZtoA);
            }
          }}
          style={{ paddingLeft: 2 }}
        >
          <Button style={{ textTransform: 'none', margin: 0 }}>
            {isActiveDesc ? 'Name Z-A' : 'Name A-Z'}
          </Button>
        </Grid>
      </Grid>
    );
  };

  const renderSortOption = ({ label, sortMode }: ISortOptions) => {
    return (
      <Grid container style={{ marginLeft: 10, marginRight: 10 }}>
        <Grid item xs={1} style={{ paddingTop: 3, paddingLeft: 6 }}>
          {activeSortMode === sortMode && <img src='/assets/shared/svg/circle.svg' width='5px' />}
        </Grid>
        <Grid
          item
          xs={11}
          onClick={() => {
            setActiveSortMode(sortMode);
            setToggleAsc(true);
          }}
          style={{ paddingLeft: 2 }}
        >
          <Button style={{ textTransform: 'none', margin: 0 }}>{label}</Button>
        </Grid>
      </Grid>
    );
  };

  const renderSortBy = () => {
    return (
      <>
        <Grid
          container
          direction='column'
          spacing={3}
          alignItems='flex-end'
          style={{ marginLeft: 5, marginRight: 1 }}
        >
          <Tooltip title='Sort By' PopperProps={{ disablePortal: true }}>
            <Button style={{ minWidth: 40, marginLeft: 10, padding: 5 }}>
              <Grid ref={sortByButtonRef}>
                <img src='/assets/shared/svg/SortByIcon.svg' width='29px' />
              </Grid>
            </Button>
          </Tooltip>
          {isOpenSort && (
            <Grid ref={sortByMenuRef}>
              <Paper
                style={{
                  position: 'absolute',
                  width: 160,
                  marginTop: 0,
                  marginLeft: -40,
                  paddingBottom: 20,
                  zIndex: 10,
                }}
              >
                <Typography className={classes.menuHeader}>Sort By</Typography>
                {!disableAlphaToggle && renderAlphaToggle()}
                {sortOptions.map(
                  (option) =>
                    (option.alwaysShow ||
                      (option.menuTitles && option.menuTitles.includes(menuTitle))) &&
                    renderSortOption(option),
                )}
              </Paper>
            </Grid>
          )}
        </Grid>
      </>
    );
  };

  const renderTopListItem = () => {
    if (!!topListItemText && topListItemClickHandler) {
      return (
        <Grid container direction='row' justify='space-between' alignItems='center'>
          <Grid item>
            <Typography
              style={{ marginLeft: 22, color: '#4952FF', fontWeight: 500, cursor: 'pointer' }}
              onClick={topListItemClickHandler}
            >
              {topListItemText}
            </Typography>
          </Grid>
        </Grid>
      );
    }
    // display select all check box
    if (props.selectAll || props.allowDenyLogicEnabled === true) {
      return (
        <div>
          <Grid container direction='row' justify='space-between' alignItems='center'>
            <Grid item>
              {/* Select All*/}
              {props.selectAll && (
                <Grid
                  container
                  direction='row'
                  justify='flex-start'
                  alignItems='center'
                  onClick={() => handleSelectAllClick(sortedList)}
                >
                  <Grid item>
                    <Checkbox
                      indeterminate={selectAllCheckedStatus === CheckBoxStatus.SEMI_CHECKED}
                      checked={selectAllCheckedStatus === CheckBoxStatus.CHECKED}
                    />
                  </Grid>
                  <Grid item>
                    <Text small bold tKey='SelectAll' />
                  </Grid>
                </Grid>
              )}
            </Grid>
            <Grid item>
              {/* Allow/DenyList*/}
              {props.allowDenyLogicEnabled && <div>{renderAllowDenyList()}</div>}
            </Grid>
          </Grid>
        </div>
      );
      // display the number of X selected and a clear button (used for realtime analytics limited select)
    } else if (
      sortedList.length > 0 &&
      !props.selectAll &&
      props.selectLimit &&
      props.selectLimit !== -1
    ) {
      return (
        <div>
          <Grid container direction='row' justify='space-between' alignItems='center'>
            <Grid
              item
              container
              direction='row'
              justify='space-between'
              alignItems='center'
              style={{
                marginBottom: 5,
              }}
            >
              <Grid item>
                <Grid container direction='column' justify='flex-start' alignItems='flex-start'>
                  <Grid item>
                    <Typography
                      style={{
                        color: '#555555',
                        fontSize: 14,
                        marginLeft: 15,
                        fontWeight: 500,
                      }}
                    >
                      {`Select up to ${props.selectLimit} ${props.menuTitle}`}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              {/* Allow/DenyList*/}
              {props.allowDenyLogicEnabled && <div>{renderAllowDenyList()}</div>}
            </Grid>
          </Grid>
        </div>
      );
    } else {
      return <Grid />;
    }
  };

  let selectedNumber = 0;
  Object.values(list).forEach((item) => {
    if (list[item.ID].selected) {
      selectedNumber++;
    }
  });

  // sort list, put selected items at the top
  const sortedList = sortFilteredList(filteredList);
  if (!isOpen) {
    return <React.Fragment />;
  }

  const { mediaSize } = useBaseState();
  const isMediumOrSmaller = isWithinMediaSizeOrBelow('medium', mediaSize);

  const getStyle = () => {
    const maxWidth = isMediumOrSmaller ? 320 : 480;
    const tempStyle: CSS.Properties = {
      position: 'absolute',
      zIndex: 999,
      maxWidth: `${maxWidth}px`,
      minWidth: '240px',
      paddingBottom: spacing.large,
    };
    switch (true) {
      case responsivePositionEnabled:
        return {
          ...tempStyle,
          top: '24px',
          ...responsiveStylePosition,
        };
      // original code but refactored to maintain original intent
      // this logic should be refactored and replaced with the next dropdown version
      case isRightSide && !!rightPos:
        tempStyle.position = 'fixed';
        tempStyle.top = `${bottomPos}px`;
        tempStyle.left = `${(rightPos || 0) - 480}px`;
        break;
      case enableFixed:
        tempStyle.position = 'fixed';
        break;
      default:
        tempStyle.position = 'absolute';
        tempStyle.marginTop = '36px';
        break;
    }
    return tempStyle;
  };

  return (
    // if it is on the right side, position should be on the right side of dropdown
    <div style={getStyle()}>
      <Paper>
        <Grid
          ref={enableStatic || !mainDropdownMenuRef ? nullRef : mainDropdownMenuRef}
          className={
            isOpen ? classes.menuOpen + formatForClassname(' ' + menuTitle) : classes.menuClosed
          }
        >
          {props.enableReadOnly && <Typography>READ ONLY</Typography>}
          <Grid
            item
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              padding: spacing.small,
            }}
          >
            {!disableSearch && (
              <TextInput
                isAutoFocused={!keepDropdownOpen}
                enableClear
                isDisabled={loading}
                initialValue={searchInput}
                width={isMediumOrSmaller ? '300px' : '460px'}
                inputSize='small'
                placeholderTKey='Search'
                handleOnChange={(input: string) => {
                  handleTextInput(input);
                }}
              />
            )}
          </Grid>
          <Grid
            container
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Grid
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                paddingLeft: 10,
              }}
            >
              <Text small tKey='NumSelected' tProps={{ count: selectedNumber }} />
              <TextButton onClick={clearSelections} style={{ marginLeft: spacing.large }}>
                <Text small tKey='Clear' style={{ color: 'inherit', fontWeight: 'inherit' }} />
              </TextButton>
            </Grid>
            <Grid
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 10,
                margin: 5,
              }}
            >
              {quickFilterMenuItems.length > 0 && renderQuickFilterMenu(quickFilterMenuItems)}
              {disableSortBy !== true && renderSortBy()}
            </Grid>
          </Grid>
          <Divider />
          {renderTopListItem()}
          <MenuList
            ref={menuListRef}
            onScroll={handleScroll}
            style={{ maxHeight: props.maxHeight, overflowY: 'scroll', height: '96%' }}
          >
            {props.loading ? (
              <MenuItem className={classes.menuItem}>
                <Typography>Loading...</Typography>
              </MenuItem>
            ) : // no results are found when searching
            sortedList.length === 0 && searchInput.length > 0 ? (
              <MenuItem className={classes.menuItem}>
                <Typography
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  No Results
                </Typography>
              </MenuItem>
            ) : sortedList.some((ele) => ele.subheader !== undefined) ? (
              createListItemWithSubheader(sortedList.slice(0, bottomIndexDisplayed)).map(
                (item: IListItemWithSubheader) => {
                  if (item === undefined) {
                    return <></>;
                  }
                  return (
                    <>
                      <ListItemText
                        style={{
                          backgroundColor: '#fafafa',
                          color: '#000000',
                          pointerEvents: 'none',
                          marginLeft: '15px',
                        }}
                      >
                        {item.subheader}
                      </ListItemText>
                      {item.selectOption.map((selectOption: IListItem) => {
                        const { title, selected, ID } = selectOption;
                        return (
                          <Tooltip
                            title={
                              disabledTooltip !== '' && selectOption.disabled
                                ? disabledTooltip || ''
                                : title
                            }
                            key={ID}
                            PopperProps={{ disablePortal: true }}
                          >
                            <Grid>
                              <MenuItem
                                disabled={selectOption.disabled}
                                className={classes.menuItem}
                                style={{ maxHeight: 35 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleItemClick(ID);
                                  setScrollPosition(
                                    menuListRef.current ? menuListRef.current.scrollTop : 0,
                                  );
                                }}
                              >
                                {props.enableMultiSelect === true || selected ? (
                                  <Checkbox
                                    checkedIcon={computeCheckboxIcon(
                                      props.allowDenyLogicEnabled,
                                      isAllowed,
                                    )}
                                    checked={selected}
                                  />
                                ) : (
                                  <Checkbox style={{ visibility: 'hidden' }} />
                                )}
                                <Typography
                                  style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontSize: '0.875rem',
                                  }}
                                >
                                  {props.showPrefixID ? `[${ID}] ${title}` : title}
                                </Typography>
                              </MenuItem>
                            </Grid>
                          </Tooltip>
                        );
                      })}
                    </>
                  );
                },
              )
            ) : (
              sortedList.slice(0, bottomIndexDisplayed).map((item: IListItem) => {
                if (item === undefined) {
                  return <></>;
                }
                const { title, selected, ID } = item;
                return (
                  <Tooltip
                    title={disabledTooltip !== '' && item.disabled ? disabledTooltip || '' : title}
                    key={ID}
                    PopperProps={{ disablePortal: true }}
                  >
                    <Grid>
                      <MenuItem
                        disabled={item.disabled}
                        className={classes.menuItem}
                        style={{ maxHeight: 35 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleItemClick(ID);
                          setScrollPosition(
                            menuListRef.current ? menuListRef.current.scrollTop : 0,
                          );
                        }}
                      >
                        {props.enableMultiSelect === true || selected ? (
                          <Checkbox
                            checkedIcon={computeCheckboxIcon(
                              props.allowDenyLogicEnabled,
                              isAllowed,
                            )}
                            checked={selected}
                          />
                        ) : (
                          <Checkbox style={{ visibility: 'hidden' }} />
                        )}
                        <Typography
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '0.875rem',
                          }}
                        >
                          {props.showPrefixID ? `[${ID}] ${title}` : title}
                        </Typography>
                      </MenuItem>
                    </Grid>
                  </Tooltip>
                );
              })
            )}
          </MenuList>
        </Grid>
      </Paper>
    </div>
  );
};

MaterialSearchDropdownMultiList.defaultProps = {
  selectAll: false,
  enableMultiSelect: true,
  maxHeight: 300,
};
